import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  CheckCircle,
  Cancel,
  Assignment,
  Refresh,
  PhotoLibrary,
  ExpandMore,
  ZoomIn,
  ZoomOut,
  Close,
  FileUpload,
  PhotoCamera,
} from '@mui/icons-material';
import { commonAPI, rentalAPI, equipmentAPI, vehicleAPI, DepartmentOption, DepartmentUserOption } from '../../services/api';
import { Rental, RentalFilters, PaginatedResponse } from '../../types/rental';
import { Equipment } from '../../types/equipment';
import { useAuth } from '../../contexts/AuthContext';

type DialogMode = 'view' | 'create' | 'edit';

const RentalPage: React.FC = () => {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);

  const toYMD = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getLast30DaysRange = () => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 30);
    return { date_from: toYMD(start), date_to: toYMD(end) };
  };

  const [rentals, setRentals] = useState<Rental[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<RentalFilters>(() => {
    const range = getLast30DaysRange();
    return { date_from: range.date_from, date_to: range.date_to };
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Dialog 狀態
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('view');
  const [editingRental, setEditingRental] = useState<Rental | null>(null);
  const [equipmentType, setEquipmentType] = useState<'vehicle' | 'equipment'>('vehicle');
  const [saving, setSaving] = useState(false);

  // 表單資料
  const [formEquipmentId, setFormEquipmentId] = useState<number>(0);
  const [formStartTime, setFormStartTime] = useState('');
  const [formEndTime, setFormEndTime] = useState('');
  const [formPurpose, setFormPurpose] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formNeedDriver, setFormNeedDriver] = useState(false);
  const [formTimePeriod, setFormTimePeriod] = useState<'day' | 'night'>('day');
  const [formMileageStart, setFormMileageStart] = useState('');
  const [formMileageEnd, setFormMileageEnd] = useState('');
  const [formStatus, setFormStatus] = useState('completed');
  const [formDepartmentId, setFormDepartmentId] = useState<number | ''>('');
  const [formApplicantId, setFormApplicantId] = useState<number | ''>('');
  const [departmentUsers, setDepartmentUsers] = useState<DepartmentUserOption[]>([]);
  const [departmentUsersLoading, setDepartmentUsersLoading] = useState(false);

  // 照片
  const [startPhotoFile, setStartPhotoFile] = useState<File | null>(null);
  const [endPhotoFile, setEndPhotoFile] = useState<File | null>(null);
  const [startPhotoPreview, setStartPhotoPreview] = useState('');
  const [endPhotoPreview, setEndPhotoPreview] = useState('');
  const startPhotoRef = useRef<HTMLInputElement>(null);
  const endPhotoRef = useRef<HTMLInputElement>(null);

  // 使用紀錄
  const [usageRecords, setUsageRecords] = useState<any[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [photoUrlMap, setPhotoUrlMap] = useState<Record<string, string>>({});
  const [photoViewer, setPhotoViewer] = useState<{ open: boolean; src: string }>({ open: false, src: '' });
  const [photoZoom, setPhotoZoom] = useState<number>(1);

  // 刪除確認
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; rental: Rental | null }>({ open: false, rental: null });

  useEffect(() => {
    fetchRentals();
  }, [filters, currentPage]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const list = await commonAPI.getDepartments();
        setDepartments(Array.isArray(list) ? list : []);
      } catch {
        setDepartments([]);
      }
    };
    loadDepartments();
  }, []);

  // Dialog 關閉時清理
  useEffect(() => {
    if (!openDialog) {
      setUsageRecords([]);
      Object.values(photoUrlMap).forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
      setPhotoUrlMap({});
      setPhotoViewer({ open: false, src: '' });
      setPhotoZoom(1);
      setStartPhotoFile(null);
      setEndPhotoFile(null);
      if (startPhotoPreview) URL.revokeObjectURL(startPhotoPreview);
      if (endPhotoPreview) URL.revokeObjectURL(endPhotoPreview);
      setStartPhotoPreview('');
      setEndPhotoPreview('');
    }
  }, [openDialog]);

  const fetchRentals = async () => {
    try {
      setLoading(true);
      const response: PaginatedResponse<Rental> = await rentalAPI.getRentals({ ...filters, page: currentPage, page_size: pageSize });
      setRentals(response.results);
      setTotalCount(response.count);
    } catch {
      showSnackbar('載入租借資料失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentUsers = async (departmentId: number) => {
    try {
      setDepartmentUsersLoading(true);
      const list = await commonAPI.getDepartmentUsers(departmentId);
      setDepartmentUsers(Array.isArray(list) ? list : []);
    } catch {
      setDepartmentUsers([]);
      showSnackbar('載入申請人失敗', 'error');
    } finally {
      setDepartmentUsersLoading(false);
    }
  };

  const handleImportDepartmentChange = async (departmentId: number | '') => {
    setFormDepartmentId(departmentId);
    setFormApplicantId('');
    setDepartmentUsers([]);
    if (departmentId !== '') {
      await fetchDepartmentUsers(departmentId);
    }
  };

  const fetchEquipment = async (type: 'vehicle' | 'equipment') => {
    try {
      const data = type === 'vehicle'
        ? await vehicleAPI.getVehicles()
        : await equipmentAPI.getEquipment();
      setEquipment(data);
    } catch {
      setEquipment([]);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFilterChange = (field: keyof RentalFilters, value: string | number) => {
    setFilters(prev => ({ ...prev, [field]: value || undefined }));
    setCurrentPage(1);
  };

  // ========= CRUD 操作 =========

  const handleOpenCreate = () => {
    setEditingRental(null);
    setDialogMode('create');
    setEquipmentType('vehicle');
    resetForm();
    const defaultDept = user?.department ? Number(user.department) : '';
    setFormDepartmentId(defaultDept);
    setFormApplicantId('');
    setDepartmentUsers([]);
    if (defaultDept !== '') {
      fetchDepartmentUsers(defaultDept);
    }
    fetchEquipment('vehicle');
    setOpenDialog(true);
  };

  const handleOpenView = async (rental: Rental) => {
    setEditingRental(rental);
    setDialogMode('view');
    populateForm(rental);
    setOpenDialog(true);
    await loadUsageRecords(rental.id, false);
  };

  const handleOpenEdit = async (rental: Rental) => {
    setEditingRental(rental);
    setDialogMode('edit');
    const eqType: 'vehicle' | 'equipment' = rental.equipment_type === 'EQU' ? 'equipment' : 'vehicle';
    setEquipmentType(eqType);
    populateForm(rental);
    if (rental.department_id) {
      await fetchDepartmentUsers(rental.department_id);
    } else {
      setDepartmentUsers([]);
    }
    fetchEquipment(eqType);
    setOpenDialog(true);
    await loadUsageRecords(rental.id, true);
  };

  const handleDelete = (rental: Rental) => {
    setDeleteDialog({ open: true, rental });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.rental) return;
    try {
      await rentalAPI.deleteRental(deleteDialog.rental.id);
      showSnackbar('租借記錄已刪除', 'success');
      fetchRentals();
    } catch {
      showSnackbar('刪除失敗', 'error');
    } finally {
      setDeleteDialog({ open: false, rental: null });
    }
  };

  const resetForm = () => {
    setFormEquipmentId(0);
    setFormStartTime('');
    setFormEndTime('');
    setFormPurpose('');
    setFormNotes('');
    setFormNeedDriver(false);
    setFormTimePeriod('day');
    setFormMileageStart('');
    setFormMileageEnd('');
    setFormStatus('completed');
    setFormDepartmentId('');
    setFormApplicantId('');
    setDepartmentUsers([]);
    setStartPhotoFile(null);
    setEndPhotoFile(null);
    setStartPhotoPreview('');
    setEndPhotoPreview('');
  };

  const populateForm = (rental: Rental) => {
    setFormEquipmentId(rental.equipment_id);
    setFormPurpose(rental.purpose);
    setFormNotes(rental.notes || '');
    setFormNeedDriver(rental.need_driver);
    setFormTimePeriod(rental.time_period);
    setFormStatus(rental.status);
    setEquipmentType(rental.equipment_type === 'EQU' ? 'equipment' : 'vehicle');
    setFormDepartmentId(rental.department_id || '');
    setFormApplicantId(rental.user_id || '');
    setFormStartTime('');
    setFormEndTime('');
    setFormMileageStart('');
    setFormMileageEnd('');
  };

  const loadUsageRecords = async (rentalId: number, forEdit = false) => {
    setUsageLoading(true);
    try {
      const res = await rentalAPI.getUsage(rentalId);
      const rawRecords: any[] = (res as any).usage || [];

      const enriched = await Promise.all(
        rawRecords.map(async (rec) => {
          const startPath = rec.mileage_start_photo || '';
          const endPath = rec.mileage_end_photo || '';
          const startUrl = startPath
            ? (startPath.startsWith('http') ? startPath : await rentalAPI.getPhotoBlobUrl(startPath).catch(() => ''))
            : '';
          const endUrl = endPath
            ? (endPath.startsWith('http') ? endPath : await rentalAPI.getPhotoBlobUrl(endPath).catch(() => ''))
            : '';
          return { ...rec, _start_url: startUrl, _end_url: endUrl };
        })
      );

      const sorted = enriched.sort((a: any, b: any) => {
        const dA = a.usage_date || '';
        const dB = b.usage_date || '';
        if (dB !== dA) return dB.localeCompare(dA);
        const tA = (a.end_time || a.start_time || '').replace(' ', 'T');
        const tB = (b.end_time || b.start_time || '').replace(' ', 'T');
        return tB.localeCompare(tA);
      });

      setUsageRecords(sorted);

      if (sorted.length > 0) {
        const first = sorted[0];
        const st = first.start_time || '';
        const et = first.end_time || '';
        setFormStartTime(st ? st.replace(' ', 'T').slice(0, 16) : '');
        setFormEndTime(et ? et.replace(' ', 'T').slice(0, 16) : '');
        setFormMileageStart(first.mileage_start != null ? String(first.mileage_start) : '');
        setFormMileageEnd(first.mileage_end != null ? String(first.mileage_end) : '');

        // 編輯模式：帶入既有照片預覽，不需重新上傳
        if (forEdit) {
          setStartPhotoFile(null);
          setEndPhotoFile(null);
          setStartPhotoPreview(first._start_url || '');
          setEndPhotoPreview(first._end_url || '');
        }
      }

      const map: Record<string, string> = {};
      sorted.forEach((rec: any) => {
        if (rec.mileage_start_photo && rec._start_url) map[rec.mileage_start_photo] = rec._start_url;
        if (rec.mileage_end_photo && rec._end_url) map[rec.mileage_end_photo] = rec._end_url;
      });
      setPhotoUrlMap(map);
    } catch {
      setUsageRecords([]);
    } finally {
      setUsageLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formEquipmentId) {
      showSnackbar('請選擇設備', 'error');
      return;
    }
    if (!formStartTime || !formEndTime) {
      showSnackbar('請填寫開始與結束時間', 'error');
      return;
    }
    if (!formPurpose.trim()) {
      showSnackbar('請填寫用途', 'error');
      return;
    }
    if (dialogMode === 'create' || dialogMode === 'edit') {
      if (formDepartmentId === '') {
        showSnackbar('請選擇團隊', 'error');
        return;
      }
      if (formApplicantId === '') {
        showSnackbar('請選擇申請人', 'error');
        return;
      }
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('equipment_id', String(formEquipmentId));
      fd.append('equipment_type', equipmentType === 'equipment' ? 'EQU' : 'VEH');
      fd.append('start_time', formStartTime);
      fd.append('end_time', formEndTime);
      fd.append('purpose', formPurpose);
      fd.append('notes', formNotes);
      fd.append('need_driver', String(formNeedDriver));
      fd.append('time_period', formTimePeriod);
      fd.append('status', formStatus);
      if (formMileageStart) fd.append('mileage_start', formMileageStart);
      if (formMileageEnd) fd.append('mileage_end', formMileageEnd);
      if (startPhotoFile) fd.append('start_photo', startPhotoFile);
      if (endPhotoFile) fd.append('end_photo', endPhotoFile);
      if (dialogMode === 'create' || dialogMode === 'edit') {
        fd.append('department_id', String(formDepartmentId));
        fd.append('user_id', String(formApplicantId));
      }

      if (dialogMode === 'create') {
        await rentalAPI.importRecord(fd);
        showSnackbar('租借記錄匯入成功', 'success');
      } else if (dialogMode === 'edit' && editingRental) {
        if (usageRecords.length > 0) {
          fd.append('usage_id', String(usageRecords[0].id));
        }
        await rentalAPI.updateWithUsage(editingRental.id, fd);
        showSnackbar('租借記錄更新成功', 'success');
      }
      setOpenDialog(false);
      fetchRentals();
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.detail || error.message || '儲存失敗';
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleStartPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setStartPhotoFile(file);
      if (startPhotoPreview) URL.revokeObjectURL(startPhotoPreview);
      setStartPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleEndPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEndPhotoFile(file);
      if (endPhotoPreview) URL.revokeObjectURL(endPhotoPreview);
      setEndPhotoPreview(URL.createObjectURL(file));
    }
  };

  // ========= 輔助函式 =========

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reserved': return 'info';
      case 'ready': return 'warning';
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'reserved': return '預約中';
      case 'ready': return '準備就緒';
      case 'active': return '使用中';
      case 'completed': return '已完成';
      case 'cancelled': return '已取消';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'reserved': return <Assignment color="info" />;
      case 'ready': return <CheckCircle color="warning" />;
      case 'active': return <CheckCircle color="success" />;
      case 'completed': return <CheckCircle color="inherit" />;
      case 'cancelled': return <Cancel color="error" />;
      default: return <Assignment />;
    }
  };

  const formatDateOnly = (dateString: string) => {
    if (!dateString) return 'N/A';
    // 資料庫已是台灣時間，直接取日期部分顯示，不做 Date 轉換
    const m = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}/${m[2]}/${m[3]}`;
    return dateString;
  };

  const isReadonly = dialogMode === 'view';

  return (
    <Box>
      {/* 篩選列 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
            <TextField
              label="開始日期"
              type="date"
              value={filters.date_from || ''}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
            <TextField
              label="結束日期"
              type="date"
              value={filters.date_to || ''}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
            <FormControl sx={{ width: { xs: '100%', sm: 160 } }}>
              <InputLabel>狀態</InputLabel>
              <Select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="狀態"
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="reserved">預約中</MenuItem>
                <MenuItem value="active">使用中</MenuItem>
                <MenuItem value="completed">已完成</MenuItem>
                <MenuItem value="cancelled">已取消</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ width: { xs: '100%', sm: 180 } }}>
              <InputLabel>部門</InputLabel>
              <Select
                value={filters.department_id || ''}
                onChange={(e) => {
                  const raw = e.target.value as number | '';
                  setFilters(prev => ({ ...prev, department_id: raw === '' ? undefined : raw }));
                  setCurrentPage(1);
                }}
                label="部門"
              >
                <MenuItem value="">全部</MenuItem>
                {departments.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { setCurrentPage(1); fetchRentals(); }}
              sx={{ height: 56 }}
            >
              重新整理
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<FileUpload />}
              onClick={handleOpenCreate}
              sx={{
                height: 56,
                px: 3,
                fontWeight: 'bold',
                fontSize: '1rem',
              }}
            >
              匯入紀錄
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 列表 */}
      <Card>
        <TableContainer sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 2 }}>
          <Table
            sx={{
              '& .MuiTableCell-root': {
                textAlign: 'center',
                border: '1px solid rgba(0,0,0,0.12)',
                fontSize: '1.05rem',
                padding: '12px 8px',
              },
              '& .MuiTableCell-head': {
                textAlign: 'center',
                border: '1px solid rgba(0,0,0,0.2)',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: '#fafafa',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>序號</TableCell>
                <TableCell>設備資訊</TableCell>
                <TableCell>申請人</TableCell>
                <TableCell>租借日期</TableCell>
                <TableCell>歸還日期</TableCell>
                <TableCell sx={{ width: 100 }}>狀態</TableCell>
                <TableCell>用途</TableCell>
                <TableCell sx={{ width: 200 }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : rentals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">暫無訂單</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rentals.map((rental, idx) => (
                  <TableRow key={rental.id} hover>
                    <TableCell>{idx + 1 + (currentPage - 1) * pageSize}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '1.05rem' }}>
                        {rental.equipment?.name || rental.equipment_name || '未知設備'}
                        {(rental.equipment?.license_plate || rental.license_plate) &&
                          `　${rental.equipment?.license_plate || rental.license_plate}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontSize: '1.05rem' }}>
                        {rental.user?.name || '未知用戶'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rental.user?.department || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDateOnly(rental.rental_date)}</TableCell>
                    <TableCell>{formatDateOnly(rental.return_date)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(rental.status)}
                        label={getStatusText(rental.status)}
                        color={getStatusColor(rental.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '1.05rem' }}>
                        {rental.purpose}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title="查看">
                          <IconButton size="small" onClick={() => handleOpenView(rental)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="編輯">
                          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(rental)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="刪除">
                          <IconButton size="small" color="error" onClick={() => handleDelete(rental)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            共 {totalCount} 筆，頁碼 {currentPage} / {Math.max(1, Math.ceil(totalCount / pageSize))}
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>
              上一頁
            </Button>
            <Button variant="outlined" onClick={() => setCurrentPage(p => (p * pageSize < totalCount ? p + 1 : p))} disabled={currentPage * pageSize >= totalCount}>
              下一頁
            </Button>
          </Box>
        </Box>
      </Card>

      {/* ===== 新增/編輯/查看 對話框 ===== */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          background: dialogMode === 'create'
            ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
            : dialogMode === 'edit'
              ? 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)'
              : 'linear-gradient(135deg, #607d8b 0%, #90a4ae 100%)',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.3rem',
        }}>
          {dialogMode === 'create' ? '匯入租借紀錄' : dialogMode === 'edit' ? '編輯租借紀錄' : '查看租借紀錄'}
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          <Box display="flex" flexDirection="column" gap={2.5}>

            {/* 匯入/編輯：先選團隊，再選申請人 */}
            {(dialogMode === 'create' || dialogMode === 'edit') && (
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControl required sx={{ flex: '1 1 220px' }}>
                  <InputLabel>團隊</InputLabel>
                  <Select
                    value={formDepartmentId}
                    label="團隊"
                    onChange={(e) => handleImportDepartmentChange(e.target.value as number | '')}
                  >
                    {departments.map(d => (
                      <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl required sx={{ flex: '1 1 220px' }} disabled={formDepartmentId === '' || departmentUsersLoading}>
                  <InputLabel>申請人</InputLabel>
                  <Select
                    value={formApplicantId}
                    label="申請人"
                    onChange={(e) => setFormApplicantId(e.target.value as number | '')}
                  >
                    {departmentUsers.length === 0 ? (
                      <MenuItem value="" disabled>
                        {departmentUsersLoading ? '載入中...' : '請先選擇團隊'}
                      </MenuItem>
                    ) : (
                      departmentUsers.map(u => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name || u.username}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* 查看：顯示團隊與申請人 */}
            {dialogMode === 'view' && editingRental && (
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>團隊：</strong>
                  {editingRental.user?.department
                    || departments.find(d => d.id === editingRental.department_id)?.name
                    || `部門ID:${editingRental.department_id}`}
                </Typography>
                <Typography variant="body2">
                  <strong>申請人：</strong>
                  {editingRental.user?.name || `用戶ID:${editingRental.user_id}`}
                </Typography>
              </Box>
            )}

            {/* 設備類型 + 設備選擇 */}
            {dialogMode === 'create' && (
              <Box display="flex" gap={2}>
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>設備類型</InputLabel>
                  <Select
                    value={equipmentType}
                    label="設備類型"
                    onChange={(e) => {
                      const v = e.target.value as 'vehicle' | 'equipment';
                      setEquipmentType(v);
                      setFormEquipmentId(0);
                      fetchEquipment(v);
                    }}
                  >
                    <MenuItem value="vehicle">車輛</MenuItem>
                    <MenuItem value="equipment">機具</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>{equipmentType === 'vehicle' ? '選擇車輛' : '選擇機具'}</InputLabel>
                  <Select
                    value={formEquipmentId || ''}
                    label={equipmentType === 'vehicle' ? '選擇車輛' : '選擇機具'}
                    onChange={(e) => setFormEquipmentId(e.target.value as number)}
                  >
                    {equipment.map(eq => (
                      <MenuItem key={eq.id} value={eq.id}>
                        {eq.name} {eq.license_plate ? `[${eq.license_plate}]` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* 查看/編輯模式的設備顯示 */}
            {dialogMode !== 'create' && editingRental && (
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {editingRental.equipment?.name || editingRental.equipment_name || '未知設備'}
                  {(editingRental.equipment?.license_plate || editingRental.license_plate) &&
                    ` - ${editingRental.equipment?.license_plate || editingRental.license_plate}`}
                </Typography>
              </Box>
            )}

            {/* 時間輸入 */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label={dialogMode === 'edit' ? '實際開始時間' : '開始時間'}
                type="datetime-local"
                value={formStartTime}
                onChange={(e) => setFormStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                InputProps={{ readOnly: isReadonly }}
                sx={{ flex: '1 1 220px' }}
              />
              <TextField
                label={dialogMode === 'edit' ? '實際結束時間' : '結束時間'}
                type="datetime-local"
                value={formEndTime}
                onChange={(e) => setFormEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                InputProps={{ readOnly: isReadonly }}
                sx={{ flex: '1 1 220px' }}
              />
            </Box>

            {/* 里程 */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label="開始里程 (km)"
                type="number"
                value={formMileageStart}
                onChange={(e) => setFormMileageStart(e.target.value)}
                InputProps={{ readOnly: isReadonly }}
                sx={{ flex: '1 1 180px' }}
              />
              <TextField
                label="結束里程 (km)"
                type="number"
                value={formMileageEnd}
                onChange={(e) => setFormMileageEnd(e.target.value)}
                InputProps={{ readOnly: isReadonly }}
                sx={{ flex: '1 1 180px' }}
              />
            </Box>

            {/* 司機 + 時段 + 狀態（編輯/新增時） */}
            {!isReadonly && (
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                {equipmentType === 'vehicle' && (
                  <FormControlLabel
                    control={<Checkbox checked={formNeedDriver} onChange={(e) => setFormNeedDriver(e.target.checked)} />}
                    label="需要司機"
                  />
                )}
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, px: 1 }}>
                  <RadioGroup row value={formTimePeriod} onChange={(e) => setFormTimePeriod(e.target.value as 'day' | 'night')}>
                    <FormControlLabel value="day" control={<Radio size="small" />} label="日" />
                    <FormControlLabel value="night" control={<Radio size="small" />} label="夜" />
                  </RadioGroup>
                </Box>
                {dialogMode === 'edit' && (
                  <FormControl sx={{ minWidth: 140 }}>
                    <InputLabel>狀態</InputLabel>
                    <Select value={formStatus} label="狀態" onChange={(e) => setFormStatus(e.target.value)}>
                      <MenuItem value="reserved">預約中</MenuItem>
                      <MenuItem value="ready">準備就緒</MenuItem>
                      <MenuItem value="active">使用中</MenuItem>
                      <MenuItem value="completed">已完成</MenuItem>
                      <MenuItem value="cancelled">已取消</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

            {/* 用途 + 備註 */}
            <TextField
              fullWidth label="用途" value={formPurpose}
              onChange={(e) => setFormPurpose(e.target.value)}
              required InputProps={{ readOnly: isReadonly }}
            />
            <TextField
              fullWidth label="備註" multiline rows={2} value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              InputProps={{ readOnly: isReadonly }}
            />

            {/* 照片上傳（匯入）／更換（編輯） */}
            {!isReadonly && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoCamera /> {dialogMode === 'edit' ? '里程照片' : '里程照片上傳'}
                </Typography>
                {dialogMode === 'edit' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    已有照片會直接顯示；不需修改時不用重新上傳，點擊照片可更換。
                  </Typography>
                )}
                <Box display="flex" gap={3} flexWrap="wrap">
                  <Box sx={{ textAlign: 'center' }}>
                    <input ref={startPhotoRef} type="file" accept="image/*" hidden onChange={handleStartPhotoChange} />
                    <Box
                      sx={{
                        width: 180, height: 130,
                        border: '2px dashed #bbb', borderRadius: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', overflow: 'hidden',
                        '&:hover': { borderColor: '#1976d2', backgroundColor: '#f5f9ff' },
                      }}
                      onClick={() => startPhotoRef.current?.click()}
                    >
                      {startPhotoPreview ? (
                        <img src={startPhotoPreview} alt="start" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ textAlign: 'center', color: '#999' }}>
                          <PhotoCamera sx={{ fontSize: 36 }} />
                          <Typography variant="caption" display="block">
                            {dialogMode === 'edit' ? '點擊更換' : '點擊上傳'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      開始里程照片{dialogMode === 'edit' && startPhotoPreview ? '（點擊更換）' : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <input ref={endPhotoRef} type="file" accept="image/*" hidden onChange={handleEndPhotoChange} />
                    <Box
                      sx={{
                        width: 180, height: 130,
                        border: '2px dashed #bbb', borderRadius: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', overflow: 'hidden',
                        '&:hover': { borderColor: '#1976d2', backgroundColor: '#f5f9ff' },
                      }}
                      onClick={() => endPhotoRef.current?.click()}
                    >
                      {endPhotoPreview ? (
                        <img src={endPhotoPreview} alt="end" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ textAlign: 'center', color: '#999' }}>
                          <PhotoCamera sx={{ fontSize: 36 }} />
                          <Typography variant="caption" display="block">
                            {dialogMode === 'edit' ? '點擊更換' : '點擊上傳'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      結束里程照片{dialogMode === 'edit' && endPhotoPreview ? '（點擊更換）' : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* 使用紀錄（僅查看模式） */}
            {dialogMode === 'view' && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoLibrary /> 使用紀錄與照片
                </Typography>
                {usageLoading ? (
                  <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
                ) : usageRecords.length > 0 ? (
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fff' }}>
                    {usageRecords.map((rec: any, idx: number) => {
                      const usageDate = rec.usage_date || '';
                      const startTime = rec.start_time || '';
                      const endTime = rec.end_time || '';
                      const mileageStart = rec.mileage_start ?? '';
                      const mileageEnd = rec.mileage_end ?? '';
                      const startPhotoUrl = rec._start_url || '';
                      const endPhotoUrl = rec._end_url || '';
                      return (
                        <Accordion key={idx} disableGutters defaultExpanded={idx === 0}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography sx={{ fontWeight: 'bold' }}>使用日期：{usageDate || '-'}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                              <Box sx={{ minWidth: 240 }}>
                                <Typography variant="body2">開始時間：{startTime || '-'}</Typography>
                                <Typography variant="body2">結束時間：{endTime || '-'}</Typography>
                                <Typography variant="body2">開始里程：{mileageStart !== '' ? mileageStart : '-'}</Typography>
                                <Typography variant="body2">結束里程：{mileageEnd !== '' ? mileageEnd : '-'}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {startPhotoUrl && (
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Box
                                      sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', cursor: 'pointer' }}
                                      onClick={() => setPhotoViewer({ open: true, src: startPhotoUrl })}
                                    >
                                      <img src={startPhotoUrl} alt="start" style={{ width: 140, height: 100, objectFit: 'cover', display: 'block' }} />
                                    </Box>
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>開始里程照片</Typography>
                                  </Box>
                                )}
                                {endPhotoUrl && (
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Box
                                      sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', cursor: 'pointer' }}
                                      onClick={() => setPhotoViewer({ open: true, src: endPhotoUrl })}
                                    >
                                      <img src={endPhotoUrl} alt="end" style={{ width: 140, height: 100, objectFit: 'cover', display: 'block' }} />
                                    </Box>
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>結束里程照片</Typography>
                                  </Box>
                                )}
                                {!startPhotoUrl && !endPhotoUrl && (
                                  <Typography variant="body2" color="text.secondary">此筆無照片</Typography>
                                )}
                              </Box>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">無使用紀錄</Typography>
                )}
              </Box>
            )}

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            {isReadonly ? '關閉' : '取消'}
          </Button>
          {!isReadonly && (
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {dialogMode === 'create' ? '確認匯入' : '更新'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 照片全螢幕檢視 */}
      <Dialog open={photoViewer.open} onClose={() => setPhotoViewer({ open: false, src: '' })} maxWidth="xl" fullWidth>
        <Box
          sx={{
            position: 'relative', p: 0, bgcolor: 'black',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '85vh', overflow: 'hidden',
          }}
          onWheel={(e) => {
            e.preventDefault();
            setPhotoZoom(z => Math.min(5, Math.max(0.2, +(z + (e.deltaY < 0 ? 0.1 : -0.1)).toFixed(2))));
          }}
        >
          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, zIndex: 2 }}>
            <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} onClick={() => setPhotoZoom(z => Math.max(0.2, +(z - 0.2).toFixed(2)))}>
              <ZoomOut />
            </IconButton>
            <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} onClick={() => setPhotoZoom(z => Math.min(5, +(z + 0.2).toFixed(2)))}>
              <ZoomIn />
            </IconButton>
            <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} onClick={() => { setPhotoZoom(1); setPhotoViewer({ open: false, src: '' }); }}>
              <Close />
            </IconButton>
          </Box>
          {photoViewer.src && (
            <img
              src={photoViewer.src} alt="photo-viewer" draggable={false}
              style={{
                maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block',
                transform: `scale(${photoZoom})`, transition: 'transform 80ms ease-out',
                transformOrigin: 'center center', userSelect: 'none',
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* 刪除確認 */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, rental: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>確認刪除</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            刪除後將同時移除此筆租借記錄及所有關聯的使用紀錄，此操作無法復原。
          </Alert>
          {deleteDialog.rental && (
            <Box>
              <Typography><strong>設備：</strong>{deleteDialog.rental.equipment?.name || deleteDialog.rental.equipment_name}</Typography>
              <Typography><strong>日期：</strong>{formatDateOnly(deleteDialog.rental.rental_date)} ~ {formatDateOnly(deleteDialog.rental.return_date)}</Typography>
              <Typography><strong>用途：</strong>{deleteDialog.rental.purpose}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, rental: null })} variant="outlined">取消</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">確認刪除</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RentalPage;
